var lib_core = require("lib-core");
var lib_geometry = require("lib-geometry");
var PosAngleData = require("./posAngleData.js");
var RectangleData = require('../svg/rectangleData.js');
var EllipseData = require('../svg/ellipseData.js');
var LayeredShapeData = require("../footprint/layeredShapeData.js");
var TextData = require('../svg/textData.js');
var PropertyData = require('../svg/propertyData.js');
var PathData = require('../svg/pathData.js');
var PathObjectData = require('../svg/pathObjectData.js');
var PathType = require('../svg/pathType.js');
var IPCRules = require('../footprint/ipcFootprintGenerator/ipcRules.js');
var cio_config = require("cio-config");

var AbstractIPCData = function(position, angle, init) {
    var self = this;
    
    this.makeObservableTreeNode();
    this.addPosAngle(position, angle);

    this.minMaxPointerMap = {};
    this.validationMap = {};
 
    //horizontal pad count
    this.horizontalPadCount = this.value("horizontalPadCount", true, cio_config.types.positive_int.validator);

    //vertical pin pitch (pitch between two adjacent pins)
    this.verticalPinPitch = this.value("verticalPinPitch", true);

    //body size
    this.bodyWidthMax = this.value("bodyWidthMax", true);
    this.bodyWidthMin = this.value("bodyWidthMin", true);
    this.bodyLengthMax = this.value("bodyLengthMax", true);
    this.bodyLengthMin = this.value("bodyLengthMin", true);
    this.bodyHeightMax = this.value("bodyHeightMax", true);
    this.bodyHeightMin = this.value("bodyHeightMin", true);

    this.densityLevel = this.value("densityLevel", true);

    //pad shape
    this.padShape = this.value("padShape", true);

    //pad roundness - percentage of min(width, height) of pad as pad's corner radius, valMin = 0%, valMax = 50%
    this.roundedPadCornerSize = this.value('roundedPadCornerSize', true, function(val) {return val >= 0 && val <= 50;});

    //mapping type of assembly outline to component body (default is mapped to max body outline)
    this.assemblyOutlineMappingTypeToBody = this.value("assemblyOutlineMappingTypeToBody", true);

    //mapping type of silkscreen to component body (default is mapped to max body outline)
    this.silkscreenMappingTypeToBody = this.value("silkscreenMappingTypeToBody", true);

    //container for pad names
    this.nameList = this.list("nameList", true);

    //3d model is not updated if any one of the following datamembers is changed
    this.update3dModelNonTriggerList = [
        "silkscreenMappingTypeToBody", 
        "assemblyOutlineMappingTypeToBody", 
        "densityLevel", 
        "padShape", 
        "roundedPadCornerSize"
    ];
    
    this.nonTransformedSegmentedGeometry = this.cachedFunction("nonTransformedSegmentedGeometry",
        this._getNonTransformedSegmentedGeometry,
        [
            this.horizontalPadCount,
            this.verticalPinPitch,
            this.bodyWidthMax,
            this.bodyWidthMin,
            this.bodyLengthMax,
            this.bodyLengthMin,
            this.bodyHeightMax,
            this.bodyHeightMin,
            this.densityLevel,
            this.padShape,
            this.roundedPadCornerSize,
            this.assemblyOutlineMappingTypeToBody,
            this.silkscreenMappingTypeToBody
        ]
    );

    this.segmentedGeometry = this.cachedFunction("segmentedGeometry",
        function (precision) {
            return self.nonTransformedSegmentedGeometry(precision)
                .translate(self.getPos())
                .rotate(self.angle(), self.getPos());
        },
        [this.nonTransformedSegmentedGeometry, this.pos, this.angle]
    );

    this.boundingBox = this.cachedFunction("boundingBox",
        function () {
            return self.segmentedGeometry().getBB();
        },
        [this.segmentedGeometry]
    );

    //set default values
    if(init) this.densityLevel(1);
    if(init) this.roundedPadCornerSize(20); //default 20% of min(pad width, pad height)
    if(init) this.assemblyOutlineMappingTypeToBody(IPCRules.assemblyOutlineMappingTypeToBody["maximum"]);
    if(init) this.silkscreenMappingTypeToBody(IPCRules.silkscreenMappingTypeToBody["maximum"]);

    this.onInit("initialize", this.initialize, 10);
};

lib_core.mixin(AbstractIPCData, PosAngleData);
lib_core.mixin(AbstractIPCData, lib_core.ObservableTreeNode);

module.exports = AbstractIPCData;

AbstractIPCData.prototype.initialize = function() {
    this.setupMinMaxPtrMap();
};

AbstractIPCData.prototype.setupMinMaxPtrMap = function() {
    this.minMaxPointerMap["bodyWidth"] = [this.bodyWidthMin, this.bodyWidthMax];
    this.minMaxPointerMap["bodyLength"] = [this.bodyLengthMin, this.bodyLengthMax];
    this.minMaxPointerMap["bodyHeight"] = [this.bodyHeightMin, this.bodyHeightMax];
    this.validationMap["bodyHeightMax"] = [this.bodyHeightMax];
    this.validationMap["bodyLengthMax"] = [this.bodyLengthMax];
    this.validationMap["bodyWidthMax"] = [this.bodyWidthMax];
    this.validationMap["verticalPinPitch"] = [this.verticalPinPitch];
};

AbstractIPCData.prototype.getMinMaxFailures = function() {
    var fails = []
    for(var key in this.minMaxPointerMap){
        if(this.minMaxPointerMap.hasOwnProperty(key)){
            // if min is greater than max
            if(this.minMaxPointerMap[key][0]() > this.minMaxPointerMap[key][1]())
                fails.push(this.minMaxPointerMap[key]);
        }  
    }          
    for(var key in this.validationMap){
        if(this.validationMap.hasOwnProperty(key)){        
            // check if zero input for max and signle inputs (with no min)
            if(this.validationMap[key][0]() == 0)
                fails.push(this.validationMap[key]);
        }
    }
    return fails;
};

/**
 * Calculate body dimensions for silkscreen. Body dimensions depends on silkscreen mapping type.
 * @return {object} Object with properties width and height
 */
AbstractIPCData.prototype.getBodyForSilkscreen = function() {
    var bodyWidth,
        bodyHeight;

    if (this.silkscreenMappingTypeToBody() === IPCRules.silkscreenMappingTypeToBody["maximum"]) {
        bodyWidth = this.bodyWidthMax();
        bodyHeight = this.bodyLengthMax();
    } else if (this.silkscreenMappingTypeToBody() === IPCRules.silkscreenMappingTypeToBody["nominal"]) {
        bodyWidth = (this.bodyWidthMin() + this.bodyWidthMax()) / 2;
        bodyHeight = (this.bodyLengthMin() + this.bodyLengthMax()) / 2;
    } else {
        bodyWidth = this.bodyWidthMin();
        bodyHeight = this.bodyLengthMin();
    }

    return {
        width: bodyWidth,
        height: bodyHeight
    }
};

/**
 * Calculate body dimensions for assembly. Body dimensions depends on assembly mapping type.
 * @return {object} Object with properties width and height
 */
AbstractIPCData.prototype.getBodyForAssembly = function() {
    var bodyWidth,
        bodyHeight;

    if (this.assemblyOutlineMappingTypeToBody() === IPCRules.assemblyOutlineMappingTypeToBody["maximum"]) {
        bodyWidth = this.bodyWidthMax();
        bodyHeight = this.bodyLengthMax();
    } else if (this.assemblyOutlineMappingTypeToBody() === IPCRules.assemblyOutlineMappingTypeToBody["nominal"]) {
        bodyWidth = (this.bodyWidthMin() + this.bodyWidthMax()) / 2;
        bodyHeight = (this.bodyLengthMin() + this.bodyLengthMax()) / 2;
    } else {
        bodyWidth = this.bodyWidthMin();
        bodyHeight = this.bodyLengthMin();
    }

    return {
        width: bodyWidth,
        height: bodyHeight
    }
};


/**
 * Calculate assembly outline geometry for selected component.
 * @param  {array} pads Pads calculated from getPads() function.
 * @return {RectangleData}
 */
AbstractIPCData.prototype.getAssemblyOutline = function(pads) {
    if (pads.length === 0) return;

    var body = this.getBodyForAssembly();
    return new RectangleData(new lib_geometry.Coord(0, 0), 0, body.width, body.height, false, IPCRules.assemblyOutlineStrokeWidth);
};

AbstractIPCData.prototype._getNonTransformedSegmentedGeometry = function () {
    var self = this;
    /**
     * Bounding box for IPC calculated footprint. This is used for zoom to fit functionality in editor 2d and for placement.
     */
    var pads = self.getPads(false),
        holes = self.getHoles(false),
        assemblyOutline = self.getAssemblyOutline(pads),
        silkscreens;

    var boundingBox = null;

    silkscreens = self.getSilkscreen(pads);

    //pads
    pads.forEach(function(pad) {
        var padBB = pad.boundingBox && pad.boundingBox();
        if(padBB) boundingBox = padBB.merge(boundingBox);
    });

    //holes
    holes.forEach(function(hole) {
        var holeBB = hole.boundingBox && hole.boundingBox();
        if(holeBB) boundingBox = holeBB.merge(boundingBox);
    });

    //assembly
    var assemblyBB = assemblyOutline && assemblyOutline.boundingBox && assemblyOutline.boundingBox();
    if(assemblyBB) boundingBox = assemblyBB.merge(boundingBox);

    //silkscreen
    silkscreens.forEach(function(s) {
        var silkscreenBB = s.boundingBox && s.boundingBox();
        if(silkscreenBB) boundingBox = silkscreenBB.merge(boundingBox);
    });

    console.assert(boundingBox);

    return boundingBox.getPoly();
};

/**
 * Update objects transformation
 * @param {array} Array of objects
 */
AbstractIPCData.prototype.updateObjectTransformation = function(objects) {
    var self = this;

    objects.forEach(function(o) {
        o.move(self.pos());
        o.rotate(self.angle(), self.pos());
    });
};

/**
 * Update pads geometry
 * @param {array} Array of pads
 * @param  {boolean} absolutePositions
 */
AbstractIPCData.prototype.updatePads = function(pads, absolutePositions) {
    var self = this;

    self.nameList.forEach(function(idx, name) {
        if (pads[idx]) {
            var res = pads[idx].name(name);
            console.assert(res, 'update pad names failed');
        }
    });

    //apply transform on pads if absolute position flag is true
    if (absolutePositions) {
        self.updateObjectTransformation(pads);
    }
};

/**
 * Create an instance of motif object (with pad stack data) and update pad shape by IPC calculated values
 * @return {PadData} Pad data.
 */
AbstractIPCData.prototype.getMotif = function() {
    throw new Error("Not implemented.");
};

/**
 * Calculate silkscreen for selected component.
 * @param  {array} pads Pads calculated from getPads() function.
 * @return {PathData}
 */
AbstractIPCData.prototype.getSilkscreen = function(pads) {
    throw new Error("Not implemented.");
};

/**
 * Calculate pads geometry
 * @param  {boolean} absolutePositions
 * @return {array} Array of pads.
 */
AbstractIPCData.prototype.getPads = function(absolutePositions) {
    return [];
};

/**
 * Calculate holes geometry
 * @param  {boolean} absolutePositions
 * @return {array} Array of holes.
 */
AbstractIPCData.prototype.getHoles = function(absolutePositions) {
    return [];
};

/**
 * Get silkscreen pin one marker
 * @param {PadData} firstPad First pad data
 * @param {object} body Body for silkscreen
 * @param {boolean} placeAtLeftOfFirstPad Is the dot marker placed at left side of first pad maintaining the clearence?
 */
AbstractIPCData.prototype.getPinOneMarker = function(firstPad, body, placeAtLeftOfFirstPad) {
    var pinMarkerSize = IPCRules.dotPinMarkerSize,
        pinMarkerPosX,
        pinMarkerPosY;

    var maintainedClearence = IPCRules.silkscreenPinMarkerDotClearance + pinMarkerSize / 2;
    var firstPadX = firstPad.getPos().x - firstPad.getTopCopper().width() / 2;

    //if pad is outside body
    if(firstPadX < -body.width / 2) {
        if(placeAtLeftOfFirstPad) {//place at left side of first pad
            pinMarkerPosX = firstPadX - maintainedClearence;
        } else {//place the pin marker at the center between first pad and body left edge respectively if clearence is maintained else place dot maintaining clearence from body
            if(Math.abs(- body.width / 2 - firstPadX) / 2 < maintainedClearence) {
                pinMarkerPosX = - body.width / 2 - maintainedClearence;
            } else {
                pinMarkerPosX = (firstPadX - body.width / 2) / 2;
            }
        }
    } else {//if pad is inside body draw dot marker maintaining clearence from body
        pinMarkerPosX = - body.width / 2 - maintainedClearence;
    }

    if(placeAtLeftOfFirstPad) {
        pinMarkerPosY = firstPad.getPos().y
    } else {
        pinMarkerPosY = firstPad.getPos().y + firstPad.getTopCopper().height() / 2 + IPCRules.silkscreenPinMarkerDotClearance + pinMarkerSize / 2;
    }

    return new EllipseData(new lib_geometry.Coord(pinMarkerPosX, pinMarkerPosY), 0, pinMarkerSize, pinMarkerSize, true, 0);
};

/**
 * Get silkscreen for body
 * @param  {object} body Body object
 * @param  {object} topPad Top pad data 
 * @param  {object} bottomPad Bottom pad data
 * @param  {array} silkscreen Array of silkscreen
 */
AbstractIPCData.prototype.populateSilkscreenForBody = function(body, topPad, bottomPad, silkscreen) {
    var topEdge = topPad.getPos().y + topPad.getTopCopper().height() / 2 + IPCRules.silkscreenClearance;
    var botEdge = bottomPad.getPos().y - bottomPad.getTopCopper().height() / 2 - IPCRules.silkscreenClearance;
    var pathData;

    //if the top edge of first pad (with clearence) is above the top edge of assembly outline (with clearence) draw lines except side lines with clearance
    if (topEdge + IPCRules.silkscreenStrokeWidth / 2 > body.height / 2) {
        //top
        pathData = new PathData(new lib_geometry.Coord(-body.width / 2, topEdge + IPCRules.silkscreenStrokeWidth / 2), IPCRules.silkscreenStrokeWidth);
        pathData.addObject(new PathObjectData(new lib_geometry.Coord(body.width / 2, topEdge + IPCRules.silkscreenStrokeWidth / 2), PathType.LINE));
        silkscreen.push(pathData);

        //bottom
        pathData = new PathData(new lib_geometry.Coord(-body.width / 2, botEdge - IPCRules.silkscreenStrokeWidth / 2), IPCRules.silkscreenStrokeWidth);
        pathData.addObject(new PathObjectData(new lib_geometry.Coord(body.width / 2, botEdge - IPCRules.silkscreenStrokeWidth / 2), PathType.LINE));
        silkscreen.push(pathData);
    } else {
        //top with sides
        pathData = new PathData(new lib_geometry.Coord(-body.width / 2, topEdge), IPCRules.silkscreenStrokeWidth);
        pathData.addObject(new PathObjectData(new lib_geometry.Coord(-body.width / 2, body.height / 2), PathType.LINE));
        pathData.addObject(new PathObjectData(new lib_geometry.Coord(body.width / 2, body.height / 2), PathType.LINE));
        pathData.addObject(new PathObjectData(new lib_geometry.Coord(body.width / 2, topEdge), PathType.LINE));
        silkscreen.push(pathData);

        //down with sides
        pathData = pathData.clone();
        pathData.flip(new lib_geometry.Line(lib_geometry.Line.HOR, new lib_geometry.Coord(0, 0)));
        silkscreen.push(pathData);
    }
};

/**
 * Get property text (>name, >value pair)
 */
AbstractIPCData.prototype.getPropertyTexts = function() {
    var bbox = this.boundingBox();

    if (!bbox) return {};

    var propConfig = IPCRules.propertyConfig;
    var propertyTexts = {};

    //create text data
    var nameTextCoord = new lib_geometry.Coord(0, 0);
    var valueTextCoord = new lib_geometry.Coord(0, 0);
    var nameText = new TextData(nameTextCoord, 0, "NAME", propConfig.font, propConfig.fontSize, propConfig.fontUnit, 0, undefined, propConfig.alignment);
    var valueText = new TextData(valueTextCoord, 0, "VALUE", propConfig.font, propConfig.fontSize, propConfig.fontUnit, 0, undefined, propConfig.alignment, "hanging");

    //update position
    nameText.move(new lib_geometry.Coord(0, bbox.y + bbox.height + propConfig.clearance));
    valueText.move(new lib_geometry.Coord(0, bbox.y - propConfig.clearance));

    //create property data
    var namePropertyData = new PropertyData(nameText);
    var valuePropertyData = new PropertyData(valueText);

    //add to top layer
    var namePropertyLayerdShapeData = new LayeredShapeData(namePropertyData, 0);
    var valuePropertyLayerdShapeData = new LayeredShapeData(valuePropertyData, 0);

    propertyTexts["name"] = namePropertyLayerdShapeData;
    propertyTexts["value"] = valuePropertyLayerdShapeData;

    return propertyTexts;
};

/**
 * Expand the IPC calculated footprint
 * @param  {boolean} deterministic
 */
AbstractIPCData.prototype.expand = function(deterministic) {
    var self = this;
    var idx = 0;
    var objects = [];
    var pos = self.pos();
    var angle = self.angle();
    var footprintdata = this.parent();
    var pads = self.getPads(false);
    var holes = self.getHoles(false);
    var silkscreen = self.getSilkscreen(pads, false);
    var assemblyOutline = self.getAssemblyOutline(pads, false);
    var propertyTexts = self.getPropertyTexts();

    self.remove();

    function assignDeterministicId(obj, identifier){
        footprintdata.root.registerNode(obj, self.$id + identifier + "-" + idx++);   // register node with deterministic id
        obj.forEachChild(function(child){
            if (child && child.isObservableTreeNode) assignDeterministicId(child);
        });
    }

    //expand pads
    pads.forEach(function(pad) {
        var cloned_pad = pad.clone();
        objects.push(cloned_pad);

        if(deterministic){
            assignDeterministicId(cloned_pad, '-p');
        }

        footprintdata.pads.add(cloned_pad);
    });

    //expand holes
    if (holes) {
        holes.forEach(function(hole) {
            var cloned_hole = hole.clone();
            objects.push(cloned_hole);

            if(deterministic){
                assignDeterministicId(cloned_hole, '-h');
            }

            footprintdata.holes.add(cloned_hole);
        });
    }

    //expand silkscreen
    idx = 0;
    silkscreen.forEach(function(s, idx) {
        var cloned_silkscreen = s.clone();
        objects.push(cloned_silkscreen);

        if(deterministic){
            assignDeterministicId(cloned_silkscreen, '-s');
        }
        
        var layeredShapeData = new LayeredShapeData(cloned_silkscreen, 0);
        footprintdata.silkscreen.push(layeredShapeData);
    });

    //expand assembly
    if (assemblyOutline) {
        idx = 0;
        var cloned_assembly = assemblyOutline.clone();
        objects.push(cloned_assembly);

        if(deterministic){
            assignDeterministicId(cloned_assembly, '-a');
        }

        footprintdata.assembly.push(cloned_assembly);
    }

    //expand property texts
    idx = 0;
    for(var prop in propertyTexts) {
        var cloned_propText = propertyTexts[prop].clone();
        objects.push(cloned_propText);

        if(deterministic){
            assignDeterministicId(cloned_propText, '-prop');
        }

        if(prop === "name") {
            footprintdata.propNames.push(cloned_propText);
        } else if(prop === "value") {
            footprintdata.propValues.push(cloned_propText);
        }
    }

    //expand custom solder paste if its available
    idx = 0;
    pads.forEach(function(pad) {
        var cloned_pad = pad.clone();

        //if pad does not have custom solder paste do not expand
        if(!cloned_pad.padstack().pasteOverride || !cloned_pad.padstack().pasteOverride()) return;

        var solderPaste = cloned_pad.getTopSolderPaste();
        if(!solderPaste) return;

        if(!Array.isArray(solderPaste)) solderPaste = [].concat(solderPaste);

        solderPaste.forEach(function(sp) {
            objects.push(sp);

            if(deterministic){
                assignDeterministicId(sp, '-sp');
            }

            footprintdata.solderPaste.add(new LayeredShapeData(sp, 0));
        });
    });

    //apply transformation of pad replicator
    objects.forEach(function(object) {
        object.move(pos);
        object.rotate(angle, pos);
    });
};

//https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/round
AbstractIPCData.prototype.precisionRound = function(number, roundOffFactor) {
  return Math.round(number * roundOffFactor) / roundOffFactor;
}